import type { ReplayContext, SpinBoundaryEndInput, SpinBoundaryStartInput } from "@slot/client-engine";

import { CurrentWinStore } from "./CurrentWinStore";

export class CurrentWinSpinBoundary {
  private readonly store: CurrentWinStore;
  private pendingHideAfterFirstReveal = false;

  public constructor(input: { store: CurrentWinStore }) {
    this.store = input.store;
  }

  public async onSpinBoundaryStart(_input: SpinBoundaryStartInput, _context: ReplayContext): Promise<void> {
    // Defer the hide until the new grid is fully painted (first
    // `revealEventFrame` after the boundary). The previous round's win
    // therefore lingers through drop-out + reveal and disappears only
    // when the new grid is on screen.
    this.pendingHideAfterFirstReveal = true;
  }

  /**
   * Called by the current-win plugin after every frame reveal.
   * Only the first call after a `spin_boundary_start` does work — the
   * flag is one-shot, so cascade reveals are no-ops.
   */
  public onPostRevealFrame(): void {
    if (!this.pendingHideAfterFirstReveal) return;
    this.pendingHideAfterFirstReveal = false;
    this.store.hide();
    this.store.clear();
  }

  public async onSpinBoundaryEnd(input: SpinBoundaryEndInput, _context: ReplayContext): Promise<void> {
    // Only positive-win boundaries do work. 0-win is silent — the
    // post-reveal hook has already hidden the panel (or will, when the
    // reveal of this round's grid finishes).
    const win = input.spinWinAmount ?? "0";
    if (!this.isPositiveMinor(win)) return;

    // Belt-and-braces — by the time the engine fires boundary-end the
    // first reveal has already happened and the flag is already false.
    this.pendingHideAfterFirstReveal = false;
    this.store.setWinMinor(win);
  }

  /**
   * Replaces `currentWin` with the feature's accumulated total at feature
   * exit. The per-spin `onSpinBoundaryEnd` updates have already parked the
   * last winning spin's amount (e.g. 1.00) — this overrides it with the
   * feature total (e.g. 8.85) so the player sees the round's grand total
   * persist through the FeatureFinishedView and into the next base spin's
   * first reveal (when `onPostRevealFrame` clears it).
   *
   * No-op on non-positive totals (zero-win feature rounds leave the panel
   * alone — the post-reveal hide path handles those).
   */
  public onFeatureExit(featureTotalMinor: string): void {
    if (!this.isPositiveMinor(featureTotalMinor)) return;
    this.pendingHideAfterFirstReveal = false;
    this.store.setWinMinor(featureTotalMinor);
  }

  private isPositiveMinor(v: string): boolean {
    return BigInt(v) > 0n;
  }
}
